/**
 * @file Live user-invite listeners for the Settings domain.
 */

import {
    collection,
    limit,
    onSnapshot,
    orderBy,
    query,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { db } from "../../app/firebase/firebase.js";
import { activeListeners, appData } from "../../app/state/state.js";

let userInvitesListener = null;

/**
 * Detaches the live user-invites listener and clears invite cache.
 */
export function detachUserInvitesListener() {
    if (!userInvitesListener) {return;}
    try {
        userInvitesListener();
    } catch (error) {
        console.warn("Error detaching user invites listener:", error);
    }

    const listenerIndex = activeListeners.indexOf(userInvitesListener);
    if (listenerIndex !== -1) {
        activeListeners.splice(listenerIndex, 1);
    }
    userInvitesListener = null;
    appData.userInvites = [];
}

/**
 * Resets invite listener state without invoking an unsubscribe callback.
 */
export function resetUserInvitesListenerState() {
    userInvitesListener = null;
    appData.userInvites = [];
}

/**
 * Keeps admin-only invite data synchronized with the Settings user tab.
 * @param {boolean} isAdmin Whether the current user is an admin.
 */
export function syncUserInvitesListener(isAdmin) {
    if (!isAdmin) {
        detachUserInvitesListener();
        return;
    }
    if (userInvitesListener) {return;}

    const invitesQuery = query(
        collection(db, "user_invites"),
        orderBy("updatedAt", "desc"),
        limit(1000),
    );
    userInvitesListener = onSnapshot(invitesQuery, (snapshot) => {
        appData.userInvites = snapshot.docs.map((snapshotDoc) => ({
            id: snapshotDoc.id,
            ...snapshotDoc.data(),
        }));
        import("./settingsController.js")
            .then((module) => module.syncUserInviteSettingsFromLiveData?.())
            .catch((error) => console.error("Error syncing live user invites:", error));
    }, (error) => {
        console.error("Error listening to user invites:", error);
        appData.userInvites = [];
    });
    activeListeners.push(userInvitesListener);
}
